import React from 'react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

import { CreativityContentData } from '../../../../types/banner';

import type { FormField } from '../../../../types/templates';
import { ConfigAccordion } from '../config-display';
import { isVideoUrlField } from '../../../../utils';
import { tokens } from '../../../../theme/tokens';
import { getCreativityForms, getTemplateByBannerType } from 'src/clients/registry';

dayjs.extend(relativeTime);
dayjs.locale('es');

interface ConfigurationCardProps {
  creativity: CreativityContentData;
  clientId: string;
}

const getFieldLabel = (key: string, bannerType: string, clientId: string, value?: unknown): string => {
  if (value && isVideoUrlField(value)) {
    return 'Source';
  }

  if (key === 'suggested_copy') return 'Suggested copy';
  if (key === 'viral_score') return 'Viral score';

  const forms = getCreativityForms(clientId);
  const form = forms[bannerType];

  if (!form || !form.fields) return key;

  const field = form.fields.find((f: FormField) => f.key === key);
  return field?.label || key.charAt(0).toUpperCase() + key.slice(1);
};

// Returns the set of keys marked as hiddenFromDetail: true in the template.
const getHiddenFieldKeys = (bannerType: string): Set<string> => {
  const template = getTemplateByBannerType(bannerType);
  if (!template?.formFields) return new Set();

  return new Set(template.formFields.filter(f => f.hiddenFromDetail === true).map(f => f.key));
};

export const ConfigurationCard: React.FC<ConfigurationCardProps> = ({ creativity, clientId }) => {
  const config = creativity.config;
  if (!config || typeof config !== 'object') return null;
  if (!creativity.bannerType) return null;

  const bannerType = creativity.bannerType;

  const mergedData: Record<string, unknown> = {
    ...(config as object as Record<string, unknown>),
    ...(creativity.suggested_copy ? { suggested_copy: creativity.suggested_copy } : {}),
    ...(typeof creativity.viral_score === 'number' ? { viral_score: creativity.viral_score } : {})
  };

  // Filter out fields hidden in the template config
  const hiddenKeys = getHiddenFieldKeys(bannerType);

  const entries = Object.entries(mergedData).filter(
    ([key, value]) => value !== undefined && value !== null && value !== '' && !hiddenKeys.has(key)
  );
  if (entries.length === 0) return null;

  const template = getTemplateByBannerType(bannerType);
  const accordionConfig = template?.renderConfig?.accordionConfig;
  const formFields: readonly FormField[] = template?.formFields ?? [];

  const getConfigValue = (key: string) => mergedData[key];

  // If accordionConfig exists, filter out hidden fields in each section
  const finalAccordionConfig = accordionConfig
    ? accordionConfig.map(section => ({
        ...section,
        fields: section.fields.filter((key: string) => !hiddenKeys.has(key))
      }))
    : [
        {
          key: 'default',
          title: 'Detalles de configuración',
          subtitle: 'Template configuration',
          fields: entries.map(([key]) => key),
          defaultOpen: true,
          color: tokens.colors.brands.blue[200]
        }
      ];

  return (
    <div
      style={{
        borderRadius: 16,
        display: 'contents'
      }}
    >
      <ConfigAccordion
        config={finalAccordionConfig}
        data={mergedData}
        formFields={formFields}
        getFieldLabel={(key: string) => getFieldLabel(key, bannerType, clientId, getConfigValue(key))}
      />
    </div>
  );
};
